import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Col, Form, Row } from "react-bootstrap";
import { useHistory, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../api/client";
import LoadingState from "../components/common/LoadingState";
import PageHeader from "../components/common/PageHeader";
import { loadActivityLookups } from "../utils/activityLookups";

const monthOptions = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December"
].map((month) => ({ value: month, label: month }));

const initialForm = {
  code: "",
  name: "",
  level: "",
  objectives: "",
  pillar_id: "",
  implementor_id: "",
  grant_id: "",
  year: new Date().getFullYear(),
  months: [],
  costugx: "",
  costusd: "",
  rate: "",
  committed_funds: "",
  expended_funds: "",
  fully_accounted_funds: ""
};

function parseNumber(value) {
  if (value === "" || value === null || value === undefined) {
    return 0;
  }

  const parsed = Number(String(value).replace(/,/g, ""));
  return Number.isNaN(parsed) ? 0 : parsed;
}

function formatNumberInput(value) {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  const normalized = String(value).replace(/,/g, "");
  if (normalized === "" || Number.isNaN(Number(normalized))) {
    return value;
  }

  return Number(normalized).toLocaleString(undefined, {
    maximumFractionDigits: normalized.includes(".") ? 2 : 0
  });
}

function normalizeNumberInput(value) {
  return value.replace(/,/g, "");
}

function ActivityFormPage() {
  const history = useHistory();
  const { uuid } = useParams();
  const isEditing = Boolean(uuid);
  const [grants, setGrants] = useState([]);
  const [implementors, setImplementors] = useState([]);
  const [pillars, setPillars] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchLookups();
  }, []);

  useEffect(() => {
    if (isEditing) {
      fetchActivity();
    }
  }, [uuid]);

  const calculatedCostUGX = useMemo(
    () => parseNumber(form.costusd) * parseNumber(form.rate),
    [form.costusd, form.rate]
  );
  const calculatedGrantBalance = useMemo(
    () => parseNumber(form.costusd) - parseNumber(form.fully_accounted_funds),
    [form.costusd, form.fully_accounted_funds]
  );

  async function fetchLookups() {
    const lookupData = await loadActivityLookups();
    setGrants(lookupData.grants);
    setImplementors(lookupData.implementors);
    setPillars(lookupData.pillars);
  }

  async function fetchActivity() {
    setLoading(true);
    setError("");

    try {
      const response = await api.get(`/activities/${uuid}`);
      const activity = response.data;
      const schedules = activity.schedules || [];
      const scheduleYear = activity.year || schedules[0]?.year || new Date().getFullYear();

      setForm({
        code: activity.code || "",
        name: activity.name || "",
        level: activity.level || "",
        objectives: activity.objectives || "",
        pillar_id: activity.pillar_id || "",
        implementor_id: activity.implementor_id || "",
        grant_id: activity.grant_id || "",
        year: scheduleYear,
        months: schedules.map((schedule) => schedule.month).filter(Boolean),
        costugx: activity.costugx ?? "",
        costusd: activity.costusd ?? "",
        rate: activity.rate ?? "",
        committed_funds: activity.committed_funds ?? "",
        expended_funds: activity.expended_funds ?? "",
        fully_accounted_funds: activity.fully_accounted_funds ?? ""
      });
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load activity.");
    } finally {
      setLoading(false);
    }
  }

  function handleFormChange(event) {
    const { name, value } = event.target;
    const financeFields = new Set([
      "costugx",
      "costusd",
      "rate",
      "committed_funds",
      "expended_funds",
      "fully_accounted_funds",
      "gavi_grant_balances"
    ]);

    setForm((current) => ({
      ...current,
      [name]: financeFields.has(name) ? normalizeNumberInput(value) : value
    }));
  }

  function numberOrNull(value) {
    return value === "" ? null : Number(value);
  }

  async function handleSubmit(event) {
    event.preventDefault();

    if (!form.months.length) {
      toast.error("Please select at least one implementation month.");
      return;
    }

    setSaving(true);

    const payload = {
      code: form.code,
      name: form.name,
      level: form.level,
      objectives: form.objectives,
      pillar_id: form.pillar_id ? Number(form.pillar_id) : null,
      implementor_id: form.implementor_id ? Number(form.implementor_id) : null,
      grant_id: form.grant_id ? Number(form.grant_id) : null,
      year: Number(form.year),
      months: form.months,
      costugx: calculatedCostUGX,
      costusd: numberOrNull(form.costusd),
      rate: numberOrNull(form.rate),
      committed_funds: numberOrNull(form.committed_funds),
      expended_funds: numberOrNull(form.expended_funds),
      fully_accounted_funds: numberOrNull(form.fully_accounted_funds),
      gavi_grant_balances: calculatedGrantBalance
    };

    try {
      if (isEditing) {
        await api.put(`/activities/${uuid}`, payload);
        toast.success("Activity updated");
      } else {
        await api.post("/activities", payload);
        toast.success("Activity created");
      }

      history.push("/activities");
    } catch (err) {
      toast.error(err.response?.data?.error || `Failed to ${isEditing ? "update" : "create"} activity.`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Row className="justify-content-center">
      <Col xs={12} xl={10}>
        <PageHeader
          title={isEditing ? "Edit Activity" : "Add Activity"}
          action={
            <Button variant="outline-secondary" onClick={() => history.push("/activities")}>
              Back to activities
            </Button>
          }
        />

        {loading ? <LoadingState label="Loading activity..." /> : null}
        {error ? <Alert variant="danger">{error}</Alert> : null}

        {!loading ? (
          <div className="content-card form-card">
            <form onSubmit={handleSubmit}>
              <Row className="g-3">
                <Col md={6}>
                  <label className="form-label">Code</label>
                  <Form.Control name="code" value={form.code} onChange={handleFormChange} required />
                </Col>
                <Col md={6}>
                  <label className="form-label">Implementation year</label>
                  <Form.Control
                    type="number"
                    name="year"
                    step="1"
                    value={form.year}
                    onChange={handleFormChange}
                    required
                  />
                  <div className="form-text">You can plan activities for future implementation years.</div>
                </Col>
                <Col xs={12}>
                  <label className="form-label">Name</label>
                  <Form.Control name="name" value={form.name} onChange={handleFormChange} required />
                </Col>
                <Col xs={12}>
                  <label className="form-label">Level</label>
                  <Form.Control name="level" value={form.level} onChange={handleFormChange} />
                </Col>
                <Col xs={12}>
                  <label className="form-label">Objectives</label>
                  <Form.Control as="textarea" rows={4} name="objectives" value={form.objectives} onChange={handleFormChange} />
                </Col>
                <Col md={4}>
                  <label className="form-label">Pillar</label>
                  <Form.Select name="pillar_id" value={form.pillar_id} onChange={handleFormChange}>
                    <option value="">Select pillar</option>
                    {pillars.map((pillar) => (
                      <option key={pillar.id} value={pillar.id}>
                        {pillar.name}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={4}>
                  <label className="form-label">Implementor</label>
                  <Form.Select name="implementor_id" value={form.implementor_id} onChange={handleFormChange}>
                    <option value="">Select implementor</option>
                    {implementors.map((implementor) => (
                      <option key={implementor.id} value={implementor.id}>
                        {implementor.name}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={4}>
                  <label className="form-label">Grant</label>
                  <Form.Select name="grant_id" value={form.grant_id} onChange={handleFormChange}>
                    <option value="">Select grant</option>
                    {grants.map((grant) => (
                      <option key={grant.id} value={grant.id}>
                        {grant.name}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                <Col xs={12}>
                  <label className="form-label">Implementation months</label>
                  <div className="activity-month-checkboxes">
                    {monthOptions.map((option) => (
                      <Form.Check
                        key={option.value}
                        id={`activity-month-${option.value}`}
                        type="checkbox"
                        label={option.label}
                        checked={form.months.includes(option.value)}
                        onChange={(event) =>
                          setForm((current) => ({
                            ...current,
                            months: event.target.checked
                              ? [...current.months, option.value]
                              : current.months.filter((month) => month !== option.value)
                          }))
                        }
                      />
                    ))}
                  </div>
                  {!form.months.length ? (
                    <div className="form-text text-danger mt-2">Select at least one implementation month before saving.</div>
                  ) : (
                    <div className="form-text mt-2">Choose one or more months to create the activity schedule.</div>
                  )}
                </Col>
                <Col md={6}>
                  <label className="form-label">Cost UGX equivalent</label>
                  <Form.Control value={formatNumberInput(calculatedCostUGX)} readOnly disabled className="calculated-input" />
                </Col>
                <Col md={6}>
                  <label className="form-label">Cost USD</label>
                  <Form.Control name="costusd" value={formatNumberInput(form.costusd)} onChange={handleFormChange} />
                </Col>
                <Col md={6}>
                  <label className="form-label">Rate</label>
                  <Form.Control name="rate" value={formatNumberInput(form.rate)} onChange={handleFormChange} />
                </Col>
                <Col md={6}>
                  <label className="form-label">Committed funds</label>
                  <Form.Control name="committed_funds" value={formatNumberInput(form.committed_funds)} onChange={handleFormChange} />
                </Col>
                {isEditing ? (
                  <Col md={6}>
                    <label className="form-label">Expended funds</label>
                    <Form.Control name="expended_funds" value={formatNumberInput(form.expended_funds)} onChange={handleFormChange} />
                  </Col>
                ) : null}
                {isEditing ? (
                  <Col md={6}>
                    <label className="form-label">Fully accounted</label>
                    <Form.Control name="fully_accounted_funds" value={formatNumberInput(form.fully_accounted_funds)} onChange={handleFormChange} />
                  </Col>
                ) : null}
                {isEditing ? (
                  <Col xs={12}>
                    <label className="form-label">Grant Balance</label>
                    <Form.Control value={formatNumberInput(calculatedGrantBalance)} readOnly disabled className="calculated-input" />
                  </Col>
                ) : null}
              </Row>

              <div className="d-flex gap-2 mt-4">
                <Button type="submit" disabled={saving}>
                  {isEditing ? "Update activity" : "Create activity"}
                </Button>
                <Button variant="outline-secondary" onClick={() => history.push("/activities")}>
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        ) : null}
      </Col>
    </Row>
  );
}

export default ActivityFormPage;
